import { SocialAuthService } from "@abacritt/angularx-social-login";
import { isPlatformBrowser } from "@angular/common";
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { TokenPayload } from "@models/finish-sign-up";
import { UserInfo } from "@models/user";
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, catchError, Observable, of, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { SKIP_ERROR_INTERCEPTOR } from "../interceptors/skip-interceptor.token";
import { LoadingService } from "./loading.service";
import { LocalStorageService } from "./local-storage.service";
import { SnackBarService } from "./snack-bar.service";
import { ConfirmationEmailRequestBody } from "./types/confirmation-request.type";
import { LoginRequestBody } from "./types/login-request.type";
import { LoginResponseBody } from "./types/login-response.type";
import { RecoveryPasswordRequestBody } from "./types/recovery-password-request";
import { RecoveryPasswordResponseBody } from "./types/recovery-password-response";
import { ResetPasswordRequestBody } from "./types/reset-password-request";
import { ResetPasswordResponseBody } from "./types/reset-password-response";
import {
  SignupRequestBody,
  SignupRequestBodyPatch,
} from "./types/signup-request.type";
import { SignupResponseBody } from "./types/signup-response.type";
import {
  SocialSignIn,
  SocialSignInResponse,
} from "./types/social-authentication.type";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly tokenKey = "auth-token";
  private readonly currentUser = "current_user";
  private readonly userName = "userName";
  private readonly userLastName = "userLastName";
  private readonly userEmail = "userEmail";
  private readonly apiBackend = environment.API_BACKEND;

  private readonly isLoggedInSubject: BehaviorSubject<boolean>;
  //INJETA AS DEPENDENCIAS QUE IRIAM NO CONSTRUCTOR
  readonly isLoggedIn: Observable<boolean>;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);

  constructor(
    private localStorageService: LocalStorageService,
    private loadingService: LoadingService,
    private snackbarService: SnackBarService,
    private socialAuthService: SocialAuthService,
  ) {
    this.loadingService.show();
    this.isLoggedInSubject = new BehaviorSubject<boolean>(!!this.getToken());
    this.isLoggedIn = this.isLoggedInSubject.asObservable();
    this.loadingService.hide();
  }

  private getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.localStorageService.getItem(this.tokenKey);
    }
    return null;
  }

  private getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  private setLocalStorage(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.localStorageService.setItem(key, value);
    }
  }

  private removeLocalStorage(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.localStorageService.removeItem(key);
    }
  }

  private clearLocalStorage() {
    this.removeLocalStorage(this.tokenKey);
    this.removeLocalStorage(this.userName);
    this.removeLocalStorage(this.userLastName);
    this.removeLocalStorage(this.userEmail);
    this.removeLocalStorage(this.currentUser);
  }

  login(loginRequestBody: LoginRequestBody): Observable<LoginResponseBody> {
    this.clearLocalStorage();

    return this.http
      .post<LoginResponseBody>(`${this.apiBackend}/v2/login`, loginRequestBody)
      .pipe(
        tap((response) => {
          if (response.status.data.mfa_required || !response.status.data.token) {
            return;
          }
          this.persistSession(response);
        }),
      );
  }

  verifyLoginMfa(challengeId: string, code: string): Observable<LoginResponseBody> {
    return this.http
      .post<LoginResponseBody>(`${this.apiBackend}/v2/login/mfa`, {
        challenge_id: challengeId,
        code,
      })
      .pipe(tap((response) => this.persistSession(response)));
  }

  resendLoginMfa(challengeId: string) {
    return this.http.post<{ message: string; channels: string[] }>(
      `${this.apiBackend}/v2/login/mfa/resend`,
      { challenge_id: challengeId },
    );
  }

  private persistSession(response: LoginResponseBody) {
    const data = response.status.data;
    if (!data.token || !data.user) {
      return;
    }

    this.setLocalStorage(this.tokenKey, data.token);
    this.setLocalStorage(this.userName, data.user.name);
    this.setLocalStorage(this.userLastName, data.user.last_name);
    this.setLocalStorage(this.userEmail, data.user.email);
    this.setLocalStorage(this.currentUser, JSON.stringify(data.user));
    this.isLoggedInSubject.next(true);
  }

  signup(signupRequestBody: SignupRequestBody): Observable<SignupResponseBody> {
    return this.http
      .post<SignupResponseBody>(`${this.apiBackend}/signup`, signupRequestBody)
      .pipe(
        tap((response) => {
          const sessionData = response?.status?.data;
          if (sessionData?.token && sessionData?.user) {
            this.setLocalStorage(this.tokenKey, sessionData.token);
            this.setLocalStorage(this.userName, sessionData.user.name ?? "");
            this.setLocalStorage(
              this.userLastName,
              sessionData.user.last_name ?? "",
            );
            this.setLocalStorage(this.userEmail, sessionData.user.email ?? "");
            this.setLocalStorage(
              this.currentUser,
              JSON.stringify(sessionData.user),
            );
            this.isLoggedInSubject.next(true);
          } else if (response.token) {
            this.setLocalStorage(this.tokenKey, response.token);
            this.setLocalStorage(
              this.userName,
              signupRequestBody.user.name ?? "",
            );
            this.setLocalStorage(
              this.userLastName,
              signupRequestBody.user.last_name ?? "",
            );
          }
        }),
      );
  }

  updateUserInfo(
    signupRequestBody: SignupRequestBody,
  ): Observable<SignupResponseBody> {
    return this.http
      .put<SignupResponseBody>(`${this.apiBackend}/signup`, signupRequestBody, {
        headers: this.getHttpHeaders(),
      })
      .pipe(
        tap((response) => {
          console.log("RESPONSE UPDATE", response);
          this.setLocalStorage(this.currentUser, JSON.stringify(response));
        }),
      );
  }

  getLocalStorageUserInfo(): UserInfo | undefined {
    const userInfo = this.localStorageService.getItem(this.currentUser);

    return userInfo ? JSON.parse(userInfo) : undefined;
  }

  updateLocalStorageUserInfo(userInfo: UserInfo): void {
    this.localStorageService.setItem(
      this.currentUser,
      JSON.stringify(userInfo),
    );
  }

  updateUserInfoPatch(
    signupRequestBody: SignupRequestBodyPatch,
  ): Observable<SignupResponseBody> {
    return this.http
      .patch<SignupResponseBody>(
        `${this.apiBackend}/signup`,
        signupRequestBody,
        { headers: this.getHttpHeaders() },
      )
      .pipe(
        tap((response) => {
          return response;
        }),
      );
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const currentUser = await this.localStorageService.getItemAsync(
        this.currentUser,
      );
      if (currentUser) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Erro ao verificar autenticação:", error);
      return false;
    }
  }

  logout(): Observable<HttpResponse<void>> {
    return this.http
      .delete<void>(`${this.apiBackend}/logout`, {
        observe: "response",
        headers: this.getHttpHeaders(),
      })
      .pipe(
        tap(() => {
          this.socialAuthService.signOut(true).then(() => {
            this.clearLocalStorage();
            this.isLoggedInSubject.next(false);
          });
        }),
      );
  }

  resetPassword(
    resetPasswordRequestBody: ResetPasswordRequestBody,
  ): Observable<ResetPasswordResponseBody> {
    return this.http.post<ResetPasswordResponseBody>(
      `${this.apiBackend}/password`,
      resetPasswordRequestBody,
    );
  }

  recoveryPassword(
    recoveryPasswordBody: RecoveryPasswordRequestBody,
  ): Observable<RecoveryPasswordResponseBody> {
    return this.http
      .put<RecoveryPasswordResponseBody>(
        `${this.apiBackend}/password`,
        recoveryPasswordBody,
      )
      .pipe(
        tap((response) => {
          console.error(response);
        }),
      );
  }

  socialSignIn(
    signInPayload: SocialSignIn,
  ): Observable<SocialSignInResponse | null> {
    return this.http
      .post<SocialSignInResponse>(
        `${this.apiBackend}/auth/social_sign_in`,
        signInPayload,
        {
          context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
        },
      )
      .pipe(
        tap((response) => {
          this.setLocalStorage(this.tokenKey, response.status.data.token);
          this.setLocalStorage(this.userName, response.status.data.user.name);
          this.setLocalStorage(
            this.userLastName,
            response.status.data.user.last_name,
          );

          this.setLocalStorage(this.userEmail, response.status.data.user.email);
          this.setLocalStorage(
            this.currentUser,
            JSON.stringify(response.status.data.user),
          );

          this.isLoggedInSubject.next(true);
        }),
        catchError(() => {
          this.snackbarService.openSnackBar(
            "Falha ao fazer login pelas redes sociais.",
            "Fechar",
            "error",
          );

          return of(null);
        }),
      );
  }

  accountConfirmation(confirmation_token: string) {
    return this.http
      .get(
        `${this.apiBackend}/confirmation?confirmation_token=${confirmation_token}`,
        { responseType: "text" },
      )
      .pipe(
        catchError((error) => {
          const errorMessage = error?.error?.error;
          if (errorMessage == "User already confirmed.") {
            this.snackbarService.openSnackBar(
              "O usuário já foi confirmado! Você já pode fazer o login.",
              "Fechar",
              "warning",
            );
          } else if (errorMessage == "User not found.") {
            this.snackbarService.openSnackBar(
              "O usuário não foi cadastrado, por favor realize o cadastro para confirmar a conta via SMS.",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Erro ao enviar código de confirmação.",
              "Fechar",
              "error",
            );
          }

          return error;
        }),
      );
  }

  requestEmailConfirmation(data: ConfirmationEmailRequestBody) {
    return this.http.post(`${this.apiBackend}/email_confirmation`, data).pipe(
      catchError((error) => {
        const errorMessage = error?.error?.error;
        if (errorMessage == "User already confirmed.") {
          this.snackbarService.openSnackBar(
            "O usuário já foi confirmado! Você já pode fazer o login.",
            "Fechar",
            "warning",
          );
        } else if (errorMessage == "User not found.") {
          this.snackbarService.openSnackBar(
            "O usuário não foi cadastrado, por favor realize o cadastro para confirmar a conta via Email.",
            "Fechar",
            "error",
          );
        } else {
          this.snackbarService.openSnackBar(
            "Falha ao enviar E-mail.",
            "Fechar",
            "error",
          );
        }

        return error;
      }),
    );
  }

  requestSMSConfirmation(data: any) {
    return this.http.post(`${this.apiBackend}/sms_confirmation`, data).pipe(
      catchError((error) => {
        const errorMessage = error.error.error;
        if (errorMessage == "User already confirmed.") {
          this.snackbarService.openSnackBar(
            "O usuário já foi confirmado! Você já pode fazer o login.",
            "Fechar",
            "warning",
          );
        } else if (errorMessage == "User not found.") {
          this.snackbarService.openSnackBar(
            "O usuário não foi cadastrado, por favor realize o cadastro para confirmar a conta via SMS.",
            "Fechar",
            "error",
          );
        } else {
          this.snackbarService.openSnackBar(
            "Falha ao enviar SMS.",
            "Fechar",
            "error",
          );
        }

        return error;
      }),
    );
  }

  requestConfirmationCode(data: any) {
    return this.http
      .post(`${this.apiBackend}/confirmation`, data, {
        responseType: "text" as "json",
      })
      .pipe(
        catchError((error) => {
          console.log(error.error);
          const errorMessage = JSON.parse(error.error).error;
          // const errorMessage = error.error.error;
          if (errorMessage === "User already confirmed.") {
            this.snackbarService.openSnackBar(
              "O usuário já foi confirmado! Você já pode fazer o login.",
              "Fechar",
              "warning",
            );
          } else if (errorMessage == "User not found.") {
            this.snackbarService.openSnackBar(
              "O usuário não foi cadastrado, por favor realize o cadastro para confirmar a conta.",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Falha ao enviar código.",
              "Fechar",
              "error",
            );
          }

          return error;
        }),
      );
  }

  getEmailToUser(): string | null {
    return this.localStorageService.getItem("userEmail");
  }

  decodeFinishSignUpToken(token: string): TokenPayload {
    return jwtDecode(token);
  }

  getCurrentUserRoles(): string[] {
    try {
      const raw = this.localStorageService.getItem(this.currentUser);
      const user = raw ? JSON.parse(raw) : null;
      return user?.roles ?? [];
    } catch {
      return [];
    }
  }
}
